/* ============================================================
   src/shared/proposalSend.ts
   ============================================================
   Pure-ish helpers for the email-a-proposal flow, extracted from
   App.tsx so the mobile shell can build the same proposals-table
   payload + send the same email without code duplication.

   Behavior contract — these helpers must produce BYTE-IDENTICAL
   output to the inline App.tsx code that previously did the work:
     - same proposals.data shape (every field in the same place)
     - same /review/{id} link
     - same subject + body template variable substitution
     - same email HTML (View Proposal button + company footer,
       direct /review link — no tracking pixel or click redirect)
     - same idempotent update-vs-insert keyed off the unique
       localStorage["du_proposal_id::{estimateId}"] (was estimateName;
       changed so different clients/estimates can never collide)
     - same supabase.functions.invoke("send-proposal-email", ...)
       payload (to / subject / html / proposalId / replyTo /
       text / cc)
   ============================================================ */

import type { SupabaseClient } from "@supabase/supabase-js";
import { readStartDate } from "./timelineStartDate";

/**
 * Customer-facing display name for an internal catalogue key.
 *
 * Spec §7: "Never print the internal key." The source PDF leaked
 * `Rail_options` and `New_Construction` straight to the customer, because the
 * proposal table printed `row.category` verbatim. Mapping lives here — one
 * place — so every render path (desktop proposal, PDF, /review) agrees.
 *
 * Underscores/hyphens become spaces and each word gets an initial capital.
 * Only the FIRST letter is touched, so an already-uppercase key such as
 * "PVC" survives intact rather than becoming "Pvc".
 */
/**
 * Canonical, lowercase unit for print (spec §7: "Units lowercase").
 *
 * Two sources disagree in this codebase: the base rows fall back to
 * hard-coded short units in ProposalPage ("sf"/"lf"/"ea"), while add-on rows
 * take whatever the pricing catalogue stores on the item, which is where a
 * long form like "each" or an uppercase "LF" comes from. Normalising here
 * means the printed table is consistent regardless of catalogue hygiene.
 */
const UNIT_ALIASES: Record<string, string> = {
  each: "ea", ea: "ea", ct: "ea", count: "ea", pc: "ea", pcs: "ea",
  lf: "lf", "lin ft": "lf", "linear ft": "lf", "linear feet": "lf",
  sf: "sf", "sq ft": "sf", sqft: "sf", "square ft": "sf", "square feet": "sf",
};

export function normalizeUnit(raw?: string | null): string {
  const u = (raw || "").trim().toLowerCase();
  if (!u) return "";
  return UNIT_ALIASES[u] || u;
}

export function displayItemName(raw?: string | null): string {
  const s = (raw || "").trim();
  if (!s) return "";
  // Any key carrying an underscore is an internal key — humanise it.
  if (s.includes("_")) {
    return s
      .replace(/_+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  // No underscore but all-lowercase ("bench", "misc") is still a key, not
  // prose — title-case it.
  if (s === s.toLowerCase()) {
    return s.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  // Anything with existing capitals is a real product name — leave it exactly
  // as entered so "Trex Enhance Naturals", "PVC" and '12" tread' survive.
  return s;
}


// ----------------------------------------------------------------
// Public types
// ----------------------------------------------------------------

export type ClientFields = {
  // clientTitle removed from the app; OPTIONAL so old snapshots and the
  // not-yet-updated mobile send sheet keep compiling. Never populated by
  // desktop anymore, so {{clientTitle}} resolves to "" (see buildEmailVars).
  clientTitle?: string;
  clientLastName: string;
  clientTown: string;
  clientEmail: string;
  // Client field expansion — OPTIONAL so existing 4-field callers (including
  // the not-yet-updated mobile send sheet) keep compiling. Desktop passes
  // them so the advertised {{clientFirstName}} placeholder resolves;
  // buildEmailVars guards each with `|| ""`, so a missing field renders blank.
  clientFirstName?: string;
  clientLocation?: string;
  clientState?: string;
  clientZip?: string;
  clientPhone?: string;
};

export type EmailVars = ClientFields & {
  userName: string;
  userPhone: string;
  userEmail: string;
  companyName: string;
};

export type ResolvedLineItems = {
  deckingType: string;
  railingType: string;
  stairsType: string;
  fastenerType: string;
  demoType: string;
  skirtingType: string;

  deckingDescription: string | null;
  railingDescription: string | null;
  stairsDescription: string | null;
  fastenerDescription: string | null;
  demoDescription: string | null;
  skirtingDescription: string | null;

  deckingQty: number;
  deckingUnit: "sf";
  railingQty: number;
  railingUnit: "lf";
  stairsQty: number;
  stairsUnit: "lf";
  fastenerQty: number;
  fastenerUnit: "ea";
  skirtingQty: number;
  skirtingUnit: "sf";
};

export type LineItemSelections = {
  selectedDeckingId: string;
  selectedRailingId: string;
  selectedStairsId: string;
  selectedFastenerId: string;
  selectedDemoId: string;
  selectedSkirtingId: string;
  deckingSqFt: number;
  railingLf: number;
  stairsCount: number;
  fastenerQtyAuto: number;
  skirtingSf: number;
};

export type ProposalPayloadInputs = {
  /** Result of desktop's buildSnapshot() (or mobile's equivalent). */
  snapshot: any;
  /** From fetchProposalSectionsSnapshot, may be null on failure. */
  sectionsSnapshot: any[] | null;
  userSettings: any;
  estimateName: string;
  finalEstimate: number;
  constructionType: string;
  client: ClientFields;
  /** Computed subtotals from computeEstimate. */
  computed: {
    deckingSubtotal: number;
    railingSubtotal: number;
    stairsSubtotal: number;
    fastenerSubtotal: number;
    demoSubtotal: number;
    skirtingSubtotal: number;
    addItemsDetailed: any[];
    upliftMultiplier: number;
    /** Ticked decking tiers resolved to totals, cheapest first. Optional:
     *  mobile has no tier panel and simply omits it, which reads downstream
     *  as "no alternate decking options on this proposal". */
    deckingTierOptions?: {
      level: string;
      total: number;
      diff?: number | null;
      isCurrent?: boolean;
    }[];
    /** Name of the tier the estimate is currently quoting — the anchor row of
     *  the proposal's Decking Upgrades table, which prints whether or not that
     *  tier is one of the ticked ones. */
    quotedDeckingTier?: string;
  };
  lineItems: ResolvedLineItems;
  /**
   * Optional override for proposalNotesSnapshot, for callers holding the live
   * Notes state (the mobile proposal screen).
   *
   * A NON-BLANK value wins over the localStorage read. A blank value ("" or
   * whitespace) is ignored and the localStorage fallback is used instead —
   * blank previously took precedence, so a caller with no Notes to hand (the
   * "email to myself" flow passed a literal "") silently blanked the saved
   * Notes on a proposal the customer was already reading. Clearing the notes
   * for real still works: the textarea clears the localStorage key too.
   *
   * Callers without live Notes state should omit this entirely.
   */
  proposalNotesSnapshotOverride?: string;
};

export type ProposalRowPayload = {
  estimate_name: string;
  data: Record<string, any>;
};

export type SaveOrUpdateResult = {
  id: string | null;
  updated: boolean;
};

// ----------------------------------------------------------------
// Template rendering — identical to App.tsx's renderTemplate
// ----------------------------------------------------------------

export function renderTemplate(
  tpl: string,
  vars: Record<string, string>
): string {
  return (tpl || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_, key) => {
    return vars[key] ?? "";
  });
}

// Render the email BODY and clean up the greeting when the name placeholder
// resolved to "". The default greeting is "Hi {{clientFirstName}}," — when the
// first name is blank that renders as "Hi ," (stray space before the comma);
// this collapses it to "Hi," so the salutation never looks broken. Scoped to
// the "Hi <spaces>," artifact so it can't disturb real body text.
export function renderEmailBody(
  tpl: string,
  vars: Record<string, string>
): string {
  let out = renderTemplate(tpl, vars);
  if (!(vars.clientFirstName || "").trim()) {
    out = out.replace(/Hi[ \t]+,/g, "Hi,");
  }
  return out;
}

// ----------------------------------------------------------------
// Sender-info gate
// ----------------------------------------------------------------
//
// Every proposal email goes out under the user's name + phone +
// reply-to email. If any of those are blank the customer gets an
// embarrassing "Thanks, " signature with no name, no callback
// number, and no reply address — and the edge function actually
// can't send at all without a reply-to. So we refuse to send
// until the user has filled in all three on the Settings page.
//
// Both desktop (handleEmailProposal) and mobile
// (MobileProposalPage's "Send Proposal" + MobileSendSheet's
// performSend) call this at their respective entry points.
//
// Each caller is responsible for surfacing the message its own
// way (toast vs. alert vs. inline status) and for routing the
// user to Settings, since those differ per shell.

export const SENDER_INFO_MISSING_MESSAGE =
  "Please fill in your name, phone, and email in Settings before sending proposals.";

export function isSenderInfoComplete(userSettings: any): boolean {
  return !!(
    (userSettings?.userName || "").trim() &&
    (userSettings?.userPhone || "").trim() &&
    (userSettings?.userEmail || "").trim()
  );
}

/** Build the {{var}} substitution map used for subject + body templates. */
export function buildEmailVars(
  client: ClientFields,
  userSettings: any
): EmailVars {
  return {
    // clientTitle intentionally omitted — renderTemplate resolves any leftover
    // {{clientTitle}} in old custom templates to "" so nothing breaks.
    clientLastName: (client.clientLastName || "").trim(),
    clientTown: (client.clientTown || "").trim(),
    clientEmail: (client.clientEmail || "").trim(),
    // Client field expansion — make the new fields available as {{...}}
    // placeholders (e.g. the already-advertised {{clientFirstName}}).
    clientFirstName: (client.clientFirstName || "").trim(),
    clientLocation: (client.clientLocation || "").trim(),
    clientState: (client.clientState || "").trim(),
    clientZip: (client.clientZip || "").trim(),
    clientPhone: (client.clientPhone || "").trim(),

    userName: (userSettings?.userName || "").trim(),
    userPhone: (userSettings?.userPhone || "").trim(),
    userEmail: (userSettings?.userEmail || "").trim(),
    companyName: (userSettings?.companyName || "").trim(),
  };
}

// ----------------------------------------------------------------
// Public base URL — review links must ALWAYS point at the public
// production web app, regardless of where the send happens.
//
// The packaged desktop app loads from a file:// origin, so we cannot
// trust window.location.origin there (it would produce a broken
// file://review/... link). We only trust the origin when it is a
// genuine public https web origin (web + mobile); in every other
// case — file:// (desktop), empty, or any non-http scheme, plus dev
// hosts — we fall back to the production URL.
// ----------------------------------------------------------------

const PRODUCTION_BASE_URL = "https://proposals.estimator.trade";

export function getPublicBaseUrl(): string {
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";

  const isPublicWebOrigin =
    origin.startsWith("http") &&
    !origin.includes("localhost") &&
    !origin.includes("github.dev") &&
    !origin.includes("codesandbox.io");

  if (!isPublicWebOrigin) {
    return PRODUCTION_BASE_URL;
  }

  return origin.replace(/\/$/, "");
}

export function buildReviewLink(proposalId: string): string {
  return `${getPublicBaseUrl()}/review/${proposalId}`;
}

// ----------------------------------------------------------------
// Line-item resolution — pricing-row lookup that becomes the
// deckingType/railingType/etc. + description + qty/unit columns
// of proposals.data
// ----------------------------------------------------------------

export function resolveProposalLineItems(
  pricingItems: any[],
  sel: LineItemSelections
): ResolvedLineItems {
  const findItem = (id: string) =>
    pricingItems.find((p: any) => String(p.id) === String(id));

  const deckRow = sel.selectedDeckingId ? findItem(sel.selectedDeckingId) : null;
  const railRow = sel.selectedRailingId ? findItem(sel.selectedRailingId) : null;
  const stairRow = sel.selectedStairsId ? findItem(sel.selectedStairsId) : null;
  const fastRow = sel.selectedFastenerId
    ? findItem(sel.selectedFastenerId)
    : null;
  const demoRow = sel.selectedDemoId ? findItem(sel.selectedDemoId) : null;
  const skirtRow = sel.selectedSkirtingId
    ? findItem(sel.selectedSkirtingId)
    : null;

  return {
    deckingType: (deckRow?.name || "").toString(),
    railingType: (railRow?.name || "").toString(),
    stairsType: (stairRow?.name || "").toString(),
    fastenerType: (fastRow?.name || "").toString(),
    demoType: (demoRow?.name || "").toString(),
    skirtingType: (skirtRow?.name || "").toString(),

    deckingDescription: (deckRow as any)?.proposal_description || null,
    railingDescription: (railRow as any)?.proposal_description || null,
    stairsDescription: (stairRow as any)?.proposal_description || null,
    fastenerDescription: (fastRow as any)?.proposal_description || null,
    demoDescription: (demoRow as any)?.proposal_description || null,
    skirtingDescription: (skirtRow as any)?.proposal_description || null,

    deckingQty: Number(sel.deckingSqFt || 0),
    deckingUnit: "sf",
    railingQty: Number(sel.railingLf || 0),
    railingUnit: "lf",
    stairsQty: Number(sel.stairsCount || 0),
    stairsUnit: "lf",
    fastenerQty: Number(sel.fastenerQtyAuto || 0),
    fastenerUnit: "ea",
    skirtingQty: Number(sel.skirtingSf || 0),
    skirtingUnit: "sf",
  };
}

// ----------------------------------------------------------------
// Fetch the org's proposal-sections snapshot (used so the emailed
// /review page has the SAME bullets/paragraphs/reviews as Print
// Proposal). Mirrors the inline fetch in App.tsx.
// ----------------------------------------------------------------

export async function fetchProposalSectionsSnapshot(
  supabase: SupabaseClient,
  orgId: string | null | undefined
): Promise<any[] | null> {
  if (!orgId) return null;
  try {
    const { data, error } = await supabase
      .from("org_proposal_sections")
      .select("*")
      .eq("org_id", orgId)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------
// Build the proposals-table row payload
// ----------------------------------------------------------------

export function buildProposalPayload(
  args: ProposalPayloadInputs
): ProposalRowPayload {
  const {
    snapshot,
    sectionsSnapshot,
    userSettings,
    estimateName,
    finalEstimate,
    constructionType,
    client,
    computed,
    lineItems,
  } = args;

  const safeName = estimateName || "Untitled Estimate";

  // Per-estimate localStorage keys — exactly the values the inline
  // code in App.tsx was writing into proposals.data.
  const startWeeksSnapshot = readNumberFromStorage(
    `du_timeline_start_weeks::${estimateName}`,
    3
  );
  const durationDaysSnapshot = readNumberFromStorage(
    `du_timeline_duration_days::${estimateName}`,
    2
  );
  // Specific start date (overrides the week range); null = weeks mode.
  const startDateSnapshot = readStartDate(estimateName) || null;
  // Permit on → the Notes section prints the permit start-date line. Frozen
  // here so the customer's copy keeps what was true when it was sent.
  const permitStartNoteSnapshot = !!(snapshot as any)?.includePermit;
  // Show Cost / Show Visuals. Read through the SHARED key helper so this
  // agrees with what the proposal screens seed their toggles from — reading a
  // key the editor never seeded from is precisely what let the button say OFF
  // while the snapshot froze ON. Absent key → false, so old proposals (and any
  // not explicitly toggled on) still coerce to OFF.
  const toggleEstimateId = (snapshot as any)?.estimateId ?? null;
  const showLineItemPricesSnapshot = readProposalToggle(
    "du_show_line_prices",
    toggleEstimateId,
    estimateName
  );
  const showVisualAppendixSnapshot = readProposalToggle(
    "du_show_visual_appendix",
    toggleEstimateId,
    estimateName
  );
  // Notes the user typed in the proposal screen's Notes section.
  //
  // Sources, in priority order:
  //   1. proposalNotesSnapshotOverride — a caller with the live React state
  //      (the mobile proposal screen) passes it directly. A BLANK override is
  //      treated as "no override" rather than an intentional erase: callers
  //      that don't have the notes to hand used to pass "" and silently blank
  //      the saved Notes. An explicit erase still works, because clearing the
  //      textarea clears the localStorage key the fallback reads.
  //   2. localStorage `du_proposal_notes::${key}` — tried under estimateId
  //      first, then estimateName. The proposal screens key notes on
  //      `estimateId || estimateName`: the id is present even before the
  //      estimate is named (the common mobile case) and is stable across a
  //      rename. The estimateName variant is the legacy location, still read
  //      so notes written by an older build aren't lost. Reading only the
  //      estimateName variant made id-keyed notes invisible to the payload.
  const proposalNotesSnapshot = resolveProposalNotes({
    estimateId: (snapshot as any)?.estimateId,
    estimateName,
    override: args.proposalNotesSnapshotOverride,
  });

  return {
    estimate_name: safeName,
    data: {
      // ✅ keep snapshot fields (good for timeline/notes keys etc.)
      ...snapshot,
      proposalSectionsSnapshot:
        sectionsSnapshot ??
        (snapshot as any)?.proposalSectionsSnapshot ??
        null,

      // ✅ REQUIRED by ProposalPage.tsx
      userSettings,

      estimateName,
      finalEstimate,
      constructionType,
      // SOW now lives in the snapshot itself (sowText). The legacy
      // sowModeSnapshot / sowCustomTextSnapshot keys are no longer
      // written — they only existed to ferry SOW state from
      // localStorage into the read-only view.
      startWeeksSnapshot,
      durationDaysSnapshot,
      startDateSnapshot,
      permitStartNoteSnapshot,
      proposalNotesSnapshot,

      clientLastName: client.clientLastName,
      clientTown: client.clientTown,
      clientEmail: client.clientEmail,
      // Client field expansion — also carried via `...snapshot` above; listed
      // explicitly here to mirror the canonical client values.
      clientFirstName: client.clientFirstName || "",
      clientLocation: client.clientLocation || "",
      clientState: client.clientState || "",
      clientZip: client.clientZip || "",
      clientPhone: client.clientPhone || "",

      deckingSubtotal: computed.deckingSubtotal,
      railingSubtotal: computed.railingSubtotal,
      stairsSubtotal: computed.stairsSubtotal,
      fastenerSubtotal: computed.fastenerSubtotal,
      demoSubtotal: computed.demoSubtotal,
      skirtingSubtotal: computed.skirtingSubtotal,

      addItemsDetailed: computed.addItemsDetailed,
      deckingTierOptions: computed.deckingTierOptions ?? [],
      quotedDeckingTier: computed.quotedDeckingTier ?? "",
      upliftMultiplier: computed.upliftMultiplier,
      showLineItemPricesSnapshot,
      showVisualAppendixSnapshot,

      deckingType: lineItems.deckingType,
      railingType: lineItems.railingType,
      stairsType: lineItems.stairsType,
      fastenerType: lineItems.fastenerType,
      demoType: lineItems.demoType,
      skirtingType: lineItems.skirtingType,
      deckingDescription: lineItems.deckingDescription,
      railingDescription: lineItems.railingDescription,
      stairsDescription: lineItems.stairsDescription,
      fastenerDescription: lineItems.fastenerDescription,
      demoDescription: lineItems.demoDescription,
      skirtingDescription: lineItems.skirtingDescription,

      deckingQty: lineItems.deckingQty,
      deckingUnit: lineItems.deckingUnit,
      railingQty: lineItems.railingQty,
      railingUnit: lineItems.railingUnit,
      stairsQty: lineItems.stairsQty,
      stairsUnit: lineItems.stairsUnit,
      fastenerQty: lineItems.fastenerQty,
      fastenerUnit: lineItems.fastenerUnit,
      skirtingQty: lineItems.skirtingQty,
      skirtingUnit: lineItems.skirtingUnit,
    },
  };
}

// ----------------------------------------------------------------
// Idempotent save: update existing proposals row if we've already
// emailed this estimateName (so the /review link stays the same),
// otherwise insert a new row.
// ----------------------------------------------------------------

/**
 * Per-estimate proposal toggles ("Show Cost" / "Show Visuals").
 *
 * ONE source of truth, shared by all three call sites: the desktop proposal
 * screen, the mobile proposal screen, and the send-time snapshot builder. They
 * previously each wrote their own template literal, which is how the desktop
 * editor and the send path ended up disagreeing.
 *
 * Keyed on `estimateId || estimateName`, matching the Notes key. The name
 * alone was the bug: a blank estimateName collapsed every unnamed estimate
 * onto the shared key `du_show_visual_appendix::`, so toggling visuals on for
 * one turned them on for all of them — the same degeneracy purgeDegenerate-
 * NotesKeys exists to clean up for Notes.
 *
 * NOTE: an estimate with neither an id nor a name still collapses to the bare
 * prefix. That is unreachable in practice (the id is assigned before a
 * proposal can be sent) and is left rather than inventing a synthetic key that
 * would not survive a reload.
 */
export type ProposalToggle = "du_show_line_prices" | "du_show_visual_appendix";

export function proposalToggleStorageKey(
  toggle: ProposalToggle,
  estimateId?: string | null,
  estimateName?: string | null
): string {
  const id = String(estimateId || "").trim();
  const name = String(estimateName || "").trim();
  return `${toggle}::${id || name}`;
}

/**
 * Read a toggle, honouring the pre-migration name-keyed value.
 *
 * Read-through rather than a one-shot migration: the id-keyed value wins when
 * present, and an estimate toggled before this change still reports its old
 * setting instead of silently snapping back to OFF. Absent/garbage → false,
 * so the default stays "hidden" exactly as before.
 */
export function readProposalToggle(
  toggle: ProposalToggle,
  estimateId?: string | null,
  estimateName?: string | null
): boolean {
  const primary = readStringFromStorage(
    proposalToggleStorageKey(toggle, estimateId, estimateName)
  );
  if (primary === "1") return true;
  if (primary === "0") return false;
  return (
    readStringFromStorage(`${toggle}::${String(estimateName || "").trim()}`) ===
    "1"
  );
}

const PROPOSAL_ID_STORAGE_PREFIX = "du_proposal_id::";

// The dedupe key is the estimate's UNIQUE id, not its (possibly blank or
// duplicated) name. A blank estimateId yields null — callers must then treat
// the proposal as brand-new (insert), never reusing another estimate's row.
export function proposalIdStorageKey(estimateId: string): string | null {
  return estimateId ? `${PROPOSAL_ID_STORAGE_PREFIX}${estimateId}` : null;
}

export function getStoredProposalId(estimateId: string): string | null {
  const key = proposalIdStorageKey(estimateId);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

export function setStoredProposalId(estimateId: string, id: string): void {
  const key = proposalIdStorageKey(estimateId);
  if (!key) return;
  try {
    localStorage.setItem(key, id);
  } catch {}
}

/**
 * Content fields that a re-save must never blank out. If the incoming payload
 * carries no value (or a blank one) for these but the stored row has one, the
 * stored value wins.
 *
 * Deliberately NOT included: sowText and projectDescription. Those pair with
 * `sowTouched` / `projectDescriptionTouched` flags precisely so an
 * intentionally-empty scope renders as empty, and preserving the old text
 * would defeat that.
 */
const PRESERVE_IF_BLANK = [
  "proposalNotesSnapshot",
  "proposalSectionsSnapshot",
  "visuals",
];

function isBlankValue(v: any): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return !v.trim();
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/**
 * Merge an incoming payload over the stored row. Keys absent from the incoming
 * payload survive via the spread; keys present but blank survive only when
 * they're in PRESERVE_IF_BLANK.
 */
export function mergeProposalData(
  existing: any,
  incoming: Record<string, any>
): Record<string, any> {
  if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
    return incoming;
  }

  const merged: Record<string, any> = { ...existing, ...incoming };

  for (const key of PRESERVE_IF_BLANK) {
    if (isBlankValue(incoming[key]) && !isBlankValue(existing[key])) {
      merged[key] = existing[key];
    }
  }

  return merged;
}

export async function saveOrUpdateProposal(
  supabase: SupabaseClient,
  payload: ProposalRowPayload,
  estimateId: string
): Promise<SaveOrUpdateResult> {
  // Dedupe ONLY when we have a unique estimateId. A blank id means we can't
  // safely identify a prior row, so always insert a fresh one rather than
  // risk overwriting a different client's proposal.
  const storageKey = proposalIdStorageKey(estimateId);
  const existingId =
    storageKey && typeof localStorage !== "undefined"
      ? localStorage.getItem(storageKey)
      : null;

  if (existingId) {
    // Non-destructive update. This row may already be live on a /review link
    // the customer is reading, so a re-save must not be able to blank content
    // it isn't deliberately changing. Previously this replaced `data`
    // wholesale, which meant any caller that built a payload without the
    // Notes to hand (e.g. the mobile "email to myself" flow) erased them from
    // a proposal that had already gone out.
    const { data: existingRow } = await supabase
      .from("proposals")
      .select("data")
      .eq("id", existingId)
      .single();

    // .select() on the update so we can see WHETHER IT HIT. A remembered id
    // whose row is gone (deleted, or wiped back when anon still held TRUNCATE)
    // matches zero rows, and a zero-row update is a success in Postgres — so
    // this used to report {updated: true} for a row that does not exist, and
    // the caller went on to email the customer /review/<that id>, which renders
    // "Proposal not found." Nothing threw and nothing was created; the dead
    // link was the only symptom, and only the customer ever saw it.
    const { data: updatedRows, error: upErr } = await supabase
      .from("proposals")
      .update({
        estimate_name: payload.estimate_name,
        data: mergeProposalData(existingRow?.data, payload.data),
      })
      .eq("id", existingId)
      .select("id");
    if (upErr) throw upErr;

    if (Array.isArray(updatedRows) && updatedRows.length > 0) {
      return { id: existingId, updated: true };
    }
    // The remembered row is gone. Fall through and insert a fresh one so the
    // link we hand the customer resolves; the new id replaces the stale one in
    // localStorage below.
  }

  const { data: ins, error: insErr } = await supabase
    .from("proposals")
    .insert(payload)
    .select("id")
    .single();
  if (insErr) throw insErr;
  const savedId = ins?.id ?? null;
  if (savedId && storageKey) {
    try {
      localStorage.setItem(storageKey, savedId);
    } catch {}
  }
  return { id: savedId, updated: false };
}

// ----------------------------------------------------------------
// Email body — HTML (View Proposal button + company footer) and the
// matching plain-text part.
//
// No tracking in the email itself: the button and the "paste this
// link" line go straight to the customer's /review page (no
// track-click redirect through the Supabase functions domain), and
// there is no open-tracking pixel. Redirects through a shared domain
// that isn't ours and hidden 1x1 images are both classic spam-filter
// signals, and proposal emails were landing in spam.
//
// The link is always the production review URL, whatever origin the
// app runs from, so a send from the desktop app or the test server
// never hands a customer a local or test-server address.
// ----------------------------------------------------------------

export function buildCustomerReviewLink(proposalId: string): string {
  const id = String(proposalId || "").trim();
  return id ? `${PRODUCTION_BASE_URL}/review/${encodeURIComponent(id)}` : "";
}

/** Company contact details shown under the View Proposal button. */
export type EmailFooter = {
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  companyWebsite?: string;
};

export function emailFooterFromSettings(userSettings: any): EmailFooter {
  return {
    companyName: userSettings?.companyName || "",
    companyPhone: userSettings?.companyPhone || "",
    companyAddress: userSettings?.companyAddress || "",
    companyWebsite: userSettings?.companyWebsite || "",
  };
}

/** A bare 10-digit US number (as often typed into Settings) reads as (631) 266-3004. */
function formatFooterPhone(value?: string): string {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
  if (/^[\d\s().+-]+$/.test(raw) && ten.length === 10) {
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return raw;
}

/** Footer lines in display order; blank fields are left out. */
export function emailFooterLines(footer?: EmailFooter | null): string[] {
  const f = footer || {};
  return [f.companyName, formatFooterPhone(f.companyPhone), f.companyAddress, f.companyWebsite]
    .map((v) => String(v || "").replace(/\s*\n\s*/g, ", ").trim())
    .filter((v) => v.length > 0);
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type BuildEmailHtmlArgs = {
  bodyText: string;
  /** proposals row id; the button links to its production /review page. */
  proposalId: string;
  footer?: EmailFooter | null;
};

export function buildEmailHtml(args: BuildEmailHtmlArgs): string {
  const { bodyText, proposalId, footer } = args;
  const proposalLink = buildCustomerReviewLink(proposalId);
  const safeLink = escapeHtml(proposalLink);
  const footerLines = emailFooterLines(footer);

  return `
        <div style="font-family: system-ui, -apple-system, Segoe UI, Arial, sans-serif; font-size: 14px; line-height: 1.6; color:#111;">
          <div style="white-space:pre-wrap;">${escapeHtml(String(bodyText))}</div>

          ${
            proposalLink
              ? `
            <div style="margin-top:18px;">
              <a href="${safeLink}"
                style="display:inline-block; background:#16a34a; color:#fff; text-decoration:none; padding:12px 16px; border-radius:10px; font-weight:700;">
                View Proposal
              </a>
            </div>
            <div style="margin-top:10px; font-size:12px; color:#555;">
              If the button doesn’t work, paste this link into your browser:<br/>
              <a href="${safeLink}" style="color:#555;">${safeLink}</a>
            </div>
          `
              : ""
          }
          ${
            footerLines.length
              ? `
            <div style="margin-top:24px; padding-top:12px; border-top:1px solid #e5e7eb; font-size:12px; line-height:1.5; color:#6b7280;">
              ${footerLines.map(escapeHtml).join("<br/>")}
            </div>
          `
              : ""
          }
        </div>
      `;
}

/**
 * The plain-text part: the message, then "View your proposal: <link>", then
 * the same company footer as the HTML.
 */
export function buildEmailText(args: BuildEmailHtmlArgs): string {
  const { bodyText, proposalId, footer } = args;
  const proposalLink = buildCustomerReviewLink(proposalId);
  const parts = [String(bodyText || "").trim()];
  if (proposalLink) parts.push(`View your proposal: ${proposalLink}`);
  const footerLines = emailFooterLines(footer);
  if (footerLines.length) parts.push(`--\n${footerLines.join("\n")}`);
  return parts.filter((p) => p.length > 0).join("\n\n");
}

// ----------------------------------------------------------------
// Edge-function invocation. Thin wrapper; takes already-built
// subject/html so the caller controls everything.
// ----------------------------------------------------------------

export type EmailAttachment = {
  filename: string;
  /** base64-encoded file content, no "data:" prefix. */
  content: string;
};

export type SendProposalEmailArgs = {
  to: string;
  subject: string;
  html: string;
  proposalId?: string;
  replyTo: string;
  text?: string;
  cc?: string[];
  /**
   * Optional file attachments (e.g. the .DUest estimate file, a PDF). The
   * edge function passes these straight through to Resend. Requires the
   * attachments-array version of the send-proposal-email function to be
   * deployed; older deploys ignore this field harmlessly.
   */
  attachments?: EmailAttachment[];
};

export async function invokeSendProposalEmail(
  supabase: SupabaseClient,
  args: SendProposalEmailArgs
): Promise<{ data: any; error: any }> {
  return supabase.functions.invoke("send-proposal-email", {
    body: {
      to: args.to,
      subject: args.subject,
      html: args.html,
      proposalId: args.proposalId,
      replyTo: args.replyTo,
      text: args.text,
      cc: args.cc,
      attachments: args.attachments,
    },
  });
}

// ----------------------------------------------------------------
// Build the proposals row + save it + return its /review link.
//
// This is the exact steps 1–4 the mobile send sheet (and desktop's
// handleEmailProposal) run before composing the email: resolve line
// items → fetch org sections → build the row payload → idempotent
// insert/update keyed on estimateId → review link. Extracted so the
// "Email to myself" flow and the client-send flow can't drift apart.
// ----------------------------------------------------------------

export type SaveProposalAndGetLinkArgs = {
  snapshot: any;
  pricingItems: any[];
  /** Output of computeEstimate(). */
  estimate: any;
  userSettings: any;
  orgId: string | null;
  /** Live notes value, when the caller has one (mobile proposal screen). */
  proposalNotes?: string;
};

export async function saveProposalAndGetLink(
  supabase: SupabaseClient,
  args: SaveProposalAndGetLinkArgs
): Promise<{ proposalId: string | null; reviewLink: string | null }> {
  const { snapshot, pricingItems, estimate, userSettings, orgId } = args;

  const client: ClientFields = {
    clientLastName: snapshot.clientLastName || "",
    clientTown: snapshot.clientTown || "",
    clientEmail: snapshot.clientEmail || "",
    clientFirstName: snapshot.clientFirstName || "",
    clientLocation: snapshot.clientLocation || "",
    clientState: snapshot.clientState || "",
    clientZip: snapshot.clientZip || "",
    clientPhone: snapshot.clientPhone || "",
  };

  const lineItems = resolveProposalLineItems(pricingItems, {
    selectedDeckingId: snapshot.selectedDeckingId || "",
    selectedRailingId: snapshot.selectedRailingId || "",
    selectedStairsId: snapshot.selectedStairsId || "",
    selectedFastenerId: snapshot.selectedFastenerId || "",
    selectedDemoId: snapshot.selectedDemoId || "",
    selectedSkirtingId: snapshot.selectedSkirtingId || "",
    deckingSqFt: Number(snapshot.deckingSqFt) || 0,
    railingLf: Number(snapshot.railingLf) || 0,
    stairsCount: Number(snapshot.stairsCount) || 0,
    fastenerQtyAuto: Number((estimate as any)?.fastenerQtyAuto) || 0,
    skirtingSf: Number(snapshot.skirtingSf) || 0,
  });

  const sectionsSnapshot = await fetchProposalSectionsSnapshot(supabase, orgId);

  const payload = buildProposalPayload({
    snapshot,
    sectionsSnapshot,
    userSettings,
    estimateName: snapshot.estimateName || "",
    finalEstimate: Number((estimate as any)?.finalEstimate) || 0,
    constructionType: snapshot.constructionType || "",
    client,
    computed: {
      deckingSubtotal: Number((estimate as any)?.deckingSubtotal) || 0,
      railingSubtotal: Number((estimate as any)?.railingSubtotal) || 0,
      stairsSubtotal: Number((estimate as any)?.stairsSubtotal) || 0,
      fastenerSubtotal: Number((estimate as any)?.fastenerSubtotal) || 0,
      demoSubtotal: Number((estimate as any)?.demoSubtotal) || 0,
      skirtingSubtotal: Number((estimate as any)?.skirtingSubtotal) || 0,
      addItemsDetailed: Array.isArray((estimate as any)?.addItemsDetailed)
        ? (estimate as any).addItemsDetailed
        : [],
      upliftMultiplier: Number((estimate as any)?.upliftMultiplier) || 1,
    },
    lineItems,
    proposalNotesSnapshotOverride: args.proposalNotes,
  });

  const { id } = await saveOrUpdateProposal(
    supabase,
    payload,
    snapshot.estimateId || ""
  );
  if (!id) return { proposalId: null, reviewLink: null };
  return { proposalId: id, reviewLink: buildReviewLink(id) };
}

// ----------------------------------------------------------------
// Internals
// ----------------------------------------------------------------

// Matches the original `Number(localStorage.getItem(key) || fallback)`
// semantics exactly — null/empty falls back, "abc" passes through to
// Number() and becomes NaN. Don't "fix" that: byte-parity.
function readNumberFromStorage(key: string, fallback: number): number {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    raw = null;
  }
  return Number(raw || fallback);
}

function readStringFromStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The localStorage key the proposal screens persist Notes under.
 *
 * The key is ALWAYS derived from the estimate's stable id — never its name.
 * A name is optional, changes under the user, and is blank on every brand-new
 * estimate, so a name-derived key collapses to the degenerate
 * "du_proposal_notes::" that every unnamed estimate would share: type a note
 * on one, start a new estimate, and the note follows you. The id is minted
 * once per estimate (App.tsx mints one on New Project) and is unique, so two
 * unnamed estimates can never collide.
 *
 * The name-derived key survives only as a READ-ONLY legacy location — see
 * legacyProposalNotesStorageKey — for notes written by builds before this.
 *
 * Blank input returns "" so callers can bail rather than probe the degenerate
 * key; every writer must treat "" as "do not persist".
 */
export function proposalNotesStorageKey(id: string): string {
  const safe = (id || "").trim();
  return safe ? `du_proposal_notes::${safe}` : "";
}

/**
 * Legacy, read-only notes key for estimates whose notes were written under the
 * NAME by a pre-id build. Only a NON-BLANK name yields a key: the blank-name
 * key is the cross-estimate leak this whole change exists to kill, so it must
 * never be read or written.
 */
export function legacyProposalNotesStorageKey(estimateName: string): string {
  return proposalNotesStorageKey((estimateName || "").trim());
}

/** Prefix every per-estimate Notes entry is stored under. */
const NOTES_KEY_PREFIX = "du_proposal_notes::";

/**
 * Delete the degenerate blank-suffix Notes keys — "du_proposal_notes::" and
 * whitespace-only variants — from storage.
 *
 * These are the poisoned entries left behind by the builds that derived the key
 * from the estimate NAME: every unnamed estimate resolved to the same key, so
 * one estimate's note became every later estimate's note. Nothing reads these
 * keys any more (proposalNotesStorageKey returns "" for a blank id, and every
 * reader skips blank keys), but a value sitting there is a live note from a
 * real customer's estimate waiting to reappear if any future code path is ever
 * less careful. Scrub it.
 *
 * Runs on every app load rather than behind a "done" marker: it is a couple of
 * string comparisons, it is idempotent, and a marker is just another way for
 * the cleanup to be skipped on the one machine that still has the bad value.
 *
 * ONLY blank-suffix keys are removed. A named legacy key
 * ("du_proposal_notes::Pahlavi") is real customer data and the migration
 * fallback still reads it — those must survive untouched.
 *
 * Returns the keys it removed, so callers can log/assert.
 */
export function purgeDegenerateNotesKeys(): string[] {
  const removed: string[] = [];
  try {
    if (typeof window === "undefined" || !window.localStorage) return removed;

    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(NOTES_KEY_PREFIX)) continue;
      // Blank / whitespace-only suffix === the shared key. Nothing else.
      if (key.slice(NOTES_KEY_PREFIX.length).trim() === "") doomed.push(key);
    }

    for (const key of doomed) {
      try {
        window.localStorage.removeItem(key);
        // Desktop mirrors some caches into electron-store; clear there too so a
        // read-through migration can't resurrect what we just deleted.
        (window as any).estimatorStore?.remove?.(key);
        removed.push(key);
      } catch {
        /* keep going — one failure must not strand the rest */
      }
    }
  } catch {
    /* storage unavailable — nothing to scrub */
  }
  return removed;
}

/**
 * The single source of truth for "what Notes belong to this estimate?", shared
 * by the payload builder (the customer /review row) and the proposal screens,
 * so the saved PDF, the printed page and /review can't disagree.
 *
 * Priority:
 *   1. `override` — a caller holding the LIVE Notes state (the proposal
 *      screens) passes it. A blank override means "no live value to hand",
 *      not "erase": callers that lacked the notes used to pass "" and blanked
 *      Notes on a proposal the customer was already reading. A real erase
 *      still works — clearing the textarea clears the id key below.
 *   2. `du_proposal_notes::<estimateId>` — the stable per-estimate key.
 *   3. `du_proposal_notes::<estimateName>` — legacy only, and only when the
 *      name is non-empty, so an unnamed estimate can never inherit another's.
 */
export function resolveProposalNotes(args: {
  estimateId?: string | null;
  estimateName?: string | null;
  override?: string | null;
}): string {
  const override = args.override;
  if (typeof override === "string" && override.trim()) return override;

  return readNotesFromCache([
    proposalNotesStorageKey(String(args.estimateId || "")),
    legacyProposalNotesStorageKey(String(args.estimateName || "")),
  ]);
}

/**
 * Read Notes from the first candidate STORAGE KEY that holds a non-empty
 * value. Blank keys are skipped — that is what keeps the degenerate
 * `du_proposal_notes::` key (shared by every unnamed estimate) unreadable.
 */
function readNotesFromCache(keys: Array<string | null | undefined>): string {
  for (const key of keys) {
    if (!key) continue;
    const value = readStringFromStorage(key);
    if (value && value.trim()) return value;
  }
  return "";
}
